import React from 'react';
import { Box, Text, Badge, HStack } from '@chakra-ui/react';
import { ITableResponse, ICatalogResponse } from '../../../../models/sessions';
import { HeatmapCell } from '../HeatmapCell';

interface DayViewProps {
  table: ITableResponse;
  catalog: ICatalogResponse;
}

/**
 * Modalità DAY — sessioni di un singolo giorno.
 * Mostra solo atleti presenti + counter "X/Y atleti presenti".
 */
export const DayTableView: React.FC<DayViewProps> = ({ table, catalog }) => {
  const columnDefs = table.meta.columns.map((id) =>
    catalog.metrics.find((m) => m.id === id),
  );
  const meta = table.meta as any;
  const dayKey = meta.day ?? table.meta.periodStart;
  const presentCount = meta.presentCount ?? table.rows.length;
  const rosterCount = meta.rosterCount ?? table.summary.athletesTotal;

  return (
    <Box>
      <HStack p={3} gap={3} bg="green.50" borderBottom="1px solid" borderColor="green.200">
        <Badge colorPalette="green" fontSize="md" px={3} py={1}>
          Giornata {dayKey}
        </Badge>
        <Text fontSize="sm" color="gray.700">
          <strong>{presentCount}</strong> presenti su <strong>{rosterCount}</strong> atleti del roster
        </Text>
      </HStack>

      {table.rows.length === 0 ? (
        <Box p={8} textAlign="center" color="gray.500">
          Nessun atleta ha sessioni in {dayKey}
        </Box>
      ) : (
        <Box overflowX="auto" bg="var(--c-card-bg)">
          <Box as="table" width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'var(--c-navy-1)', color: 'var(--c-card-bg)' }}>
                <th style={{ padding: '10px', textAlign: 'left', minWidth: 200 }}>Atleta</th>
                {columnDefs.map((def) => (
                  <th
                    key={def?.id}
                    style={{
                      padding: '8px',
                      textAlign: 'center',
                      fontSize: 10,
                      textTransform: 'uppercase',
                    }}
                  >
                    {def?.label}
                    <div style={{ fontSize: 9, opacity: 0.7 }}>
                      {def?.unit}
                      {table.meta.unitMode === 'per_min' && def?.isVolumeMetric ? '/min' : ''}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, idx) => (
                <tr key={row.athleteId} style={{ background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)' }}>
                  <td style={{ padding: 10, fontWeight: 500 }}>
                    {row.athleteName}
                    {row.position && (
                      <Text fontSize="xs" color="gray.500">{row.position}</Text>
                    )}
                  </td>
                  {columnDefs.map((def) => (
                    <td key={def?.id} style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
                      <HeatmapCell
                        value={def ? row.values[def.id] : undefined}
                        strategy={def ? table.meta.heatmap[def.id] : undefined}
                        decimals={def?.decimals ?? 0}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
};
